// @flow
import * as React from 'react';
import type { ChildrenArray } from 'react';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import { Language, English, Spanish } from '../../languages.js';
import { productColor, fontFamilies } from '../../designSystem.js';
import { InternalHeading } from '../../InternalHeading.js';
import { getNamespacedId } from './idea-navigation.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';
import { RSLWorkedQuestion } from './RSLWorkedQuestion.js';
import { RSLGeogebra } from './RSLGeogebra.js';
import { usePrintMode } from '../../jsx-activity/usePrintMode.js';
import { RSLWistiaVideo } from './RSLWistiaVideo.js';
import { RSLBunnyVideo } from './RSLBunnyVideo.js';
import { sharedRSLChildrenElementTypes } from '../common-children-types.js';
import type { SharedRSLChildren } from '../common-children-types.js';

export function RSLIntroduction(props: {|
  children: ChildrenArray<
    | SharedRSLChildren
    | React$Element<typeof RSLWistiaVideo>
    | React$Element<typeof RSLBunnyVideo>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, RSLIntroduction);

  if (failedValidation) return null;

  return (
    <div>
      <InternalHeading level={2}>
        <Language>
          <English>Introduction</English>
          <Spanish>Introducción</Spanish>
        </Language>
      </InternalHeading>
      {props.children}
    </div>
  );
}
RSLIntroduction.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      ...sharedRSLChildrenElementTypes,
      t.elementType(RSLWistiaVideo),
      t.elementType(RSLBunnyVideo),
    ),
  ),
};

export function RSLIdeaSection(props: {|
  // TODO having this as a string might be an issue. Are they going to try
  // to put mathematics in here?
  title: string,
  // NOTE: we require a content-author-provided id so that these can be
  // *relatively* stable for students who may copy links to certain sections.
  // If the ids were generated as a function of content or title, they
  // would change more frequently than would be desireable.
  id: string,
  children: ChildrenArray<
    | React$Element<typeof RSLIdeaTheory>
    | React$Element<typeof RSLIdeaWorkedQuestions>
    | React$Element<typeof RSLIdeaSummary>,
  >,
  _numPrecedingQuestions: number,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    RSLIdeaSection,
  );

  React.useEffect(() => {
    if (!props._performValidation) return;
    if (failedValidation) return;

    if (props.id.match(/^[a-z]+(-[a-z]+)*$/) === null) {
      addErrors([
        {
          message:
            '"id" must only use lowercase letters, with words separated by a single dash. Eg. "segmented-sampling"',
          ...locations.id,
        },
      ]);
    }
  });

  if (failedValidation) return null;

  const mappedChildren = React.Children.map(props.children, (child) => {
    if (child.type !== RSLIdeaWorkedQuestions) return child;
    const _child: React$Element<typeof RSLIdeaWorkedQuestions> = (child: any);
    const { _numPrecedingQuestions } = props;
    return React.cloneElement(_child, { _numPrecedingQuestions });
  });

  return (
    <section id={getNamespacedId(props.id)}>
      <InternalHeading level={2}>{props.title}</InternalHeading>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 25 }}>
        {mappedChildren}
      </div>
    </section>
  );
}
RSLIdeaSection.spec = {
  title: t.string,
  id: t.string,
  children: childrenArrayMatcher(
    { min: 0, max: 1, elementType: t.elementType(RSLIdeaTheory) },
    { min: 0, max: 1, elementType: t.elementType(RSLIdeaWorkedQuestions) },
    { min: 0, max: 1, elementType: t.elementType(RSLIdeaSummary) },
  ),
};

export function RSLIdeaTheory(props: {|
  children: ChildrenArray<
    | SharedRSLChildren
    | React$Element<typeof RSLIdeaExploration>
    | React$Element<typeof RSLInteractiveIdeaExploration>
    | React$Element<typeof RSLWistiaVideo>
    | React$Element<typeof RSLBunnyVideo>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors } = usePropsValidation(
    props,
    RSLIdeaTheory,
  );

  // Ensure only one exploration component is used.
  React.useEffect(() => {
    if (!props._performValidation) return;
    if (failedValidation) return;

    const ideaExplorationElements: Array<
      | React$Element<typeof RSLIdeaExploration>
      | React$Element<typeof RSLInteractiveIdeaExploration>,
    > = (React.Children.toArray(props.children).filter(
      (child) =>
        child.type === RSLIdeaExploration ||
        child.type === RSLInteractiveIdeaExploration,
    ): any); // Cast to any as Flow can't track what the filter does to the type

    if (ideaExplorationElements.length > 1) {
      const errors = ideaExplorationElements.slice(1).map((el) => ({
        message: `Only 1 <${RSLIdeaExploration.name}> or 1 <${RSLInteractiveIdeaExploration.name}> is allowed in <${RSLIdeaTheory.name}>`,
        // $FlowIssue doesn't think _sourceLocations is guaranteed for every element
        ...el.props._sourceLocations?.[el.type.name],
      }));
      addErrors(errors);
    }
  });

  if (failedValidation) return null;

  return <div>{props.children}</div>;
}
RSLIdeaTheory.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      ...sharedRSLChildrenElementTypes,
      t.elementType(RSLIdeaExploration),
      t.elementType(RSLInteractiveIdeaExploration),
      t.elementType(RSLWistiaVideo),
      t.elementType(RSLBunnyVideo),
    ),
  ),
};

export function RSLIdeaExploration(props: {|
  children: ChildrenArray<SharedRSLChildren>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, RSLIdeaExploration);
  if (failedValidation) return null;

  return (
    <div
      style={{
        // TODO this is NOT a sunflower color, its a teacher-only
        // color from classic mathspace
        backgroundColor: '#F3F9FF', // teacher/aliceBlue
        borderTop: '2px solid',
        borderBottom: '2px solid',
        // TODO this is NOT a sunflow colors, its a teacher-ronly
        // color from classic mathspace
        borderColor: '#195E8C', // teacher/matisse
        padding: '0 21px 16px 21px',
        margin: '25px 0',
      }}
    >
      <InternalHeading level={3}>
        <Language>
          <English>Exploration</English>
          <Spanish>Exploración</Spanish>
        </Language>
      </InternalHeading>
      <div>{props.children}</div>
    </div>
  );
}
RSLIdeaExploration.spec = {
  children: t.childrenArray(t.unionOfTypes(...sharedRSLChildrenElementTypes)),
};

export function RSLInteractiveIdeaExploration(props: {|
  children: ChildrenArray<
    SharedRSLChildren | React$Element<typeof RSLGeogebra>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    RSLInteractiveIdeaExploration,
  );
  const inPrintMode = usePrintMode();

  // Ensure there is precisely one RSLGeogebra used
  React.useEffect(() => {
    if (!props._performValidation) return;
    if (failedValidation) return;

    const geogebraElements: Array<React$Element<typeof RSLGeogebra>> =
      (React.Children.toArray(props.children).filter(
        (child) => child.type === RSLGeogebra,
      ): any); // Cast to any as Flow can't track what the filter does to the type

    if (geogebraElements.length !== 1) {
      addErrors([
        {
          // $FlowIssue function typedef is missing .name
          message: `Precisely 1 <${RSLGeogebra.name}> must be used as a child`,
          ...locations[RSLInteractiveIdeaExploration.name],
        },
      ]);
    }
  });

  if (failedValidation) return null;
  if (inPrintMode) return null;

  return (
    <div
      style={{
        // TODO this is NOT a sunflower color, its a teacher-only
        // color from classic mathspace
        backgroundColor: '#F3F9FF', // teacher/aliceBlue
        borderTop: '2px solid',
        borderBottom: '2px solid',
        // TODO this is NOT a sunflow colors, its a teacher-ronly
        // color from classic mathspace
        borderColor: '#195E8C', // teacher/matisse
        padding: '0 21px 16px 21px',
        margin: '25px 0',
      }}
    >
      <InternalHeading level={3}>
        <Language>
          <English>Exploration</English>
          <Spanish>Exploración</Spanish>
        </Language>
      </InternalHeading>
      <div>{props.children}</div>
    </div>
  );
}
RSLInteractiveIdeaExploration.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      ...sharedRSLChildrenElementTypes,
      t.elementType(RSLGeogebra),
    ),
  ),
};

export function RSLIdeaWorkedQuestions(props: {|
  children: ChildrenArray<React$Element<typeof RSLWorkedQuestion>>,
  _numPrecedingQuestions: number,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLIdeaWorkedQuestions,
  );
  if (failedValidation) return null;

  const mappedChildren = React.Children.map(props.children, (child, index) =>
    React.cloneElement(child, { _index: props._numPrecedingQuestions + index }),
  );

  return (
    <div>
      <InternalHeading level={3}>
        <Language>
          <English>Examples</English>
          <Spanish>Ejemplos</Spanish>
        </Language>
      </InternalHeading>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        {mappedChildren}
      </div>
    </div>
  );
}
RSLIdeaWorkedQuestions.spec = {
  children: t.childrenArray(t.elementType(RSLWorkedQuestion)),
};

export function RSLIdeaSummary(props: {|
  children: ChildrenArray<SharedRSLChildren>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, RSLIdeaSummary);
  if (failedValidation) return null;

  return (
    <div
      style={{
        padding: '24px 16px',
        background: productColor.aliceBlue,
        borderRadius: 8,
        border: `2px solid ${productColor.tropicalBlue}`,
        color: productColor.matisse,
        display: 'flex',
        gap: 16,
        position: 'relative',
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <LightbulbIcon />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div
          style={{
            fontFamily: fontFamilies.gilroy.name,
            fontWeight: fontFamilies.gilroy.weights.extraBold,
            fontSize: 20,
            // This vertically centers the title with the icon
            marginTop: 7,
          }}
        >
          <Language>
            <English>Idea summary</English>
            <Spanish>Resumen de la idea</Spanish>
          </Language>
        </div>
        <div>{props.children}</div>
      </div>
      <div style={{ position: 'absolute', top: -35, left: -40 }}>
        <svg width="52" height="58" viewBox="0 0 52 58" fill="none">
          <path
            d="M27.8755 46.9523C22.0486 48.6781 16.5522 49.9598 10.4745 49.9757"
            stroke={productColor.lochmara}
            strokeWidth="4.72281"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M33.3307 30.6743C27.1986 28.2703 21.623 24.9544 15.6707 22.2451"
            stroke={productColor.lochmara}
            strokeWidth="4.72281"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M44.993 23.0845C41.4061 15.5222 37.9603 8.60429 32.5406 2.90971"
            stroke={productColor.lochmara}
            strokeWidth="4.72281"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div style={{ position: 'absolute', bottom: -45, right: -40 }}>
        <svg width="58" height="52" viewBox="0 0 58 52" fill="none">
          <path
            d="M11.8782 49.4261C11.3563 43.7133 10.5785 38.364 9.30154 32.6954C8.96334 31.1941 9.71433 26.7225 8.41284 27.7153"
            stroke={productColor.lochmara}
            strokeWidth="4.72281"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M32.9253 35.7467C28.4256 30.0753 23.303 25.4689 18.9089 19.7086"
            stroke={productColor.lochmara}
            strokeWidth="4.72281"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M29.423 11.6882C38.0111 12.801 46.4886 13.1415 55.0413 14.1239"
            stroke={productColor.lochmara}
            strokeWidth="4.72281"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}
RSLIdeaSummary.spec = {
  children: t.childrenArray(t.unionOfTypes(...sharedRSLChildrenElementTypes)),
};

function LightbulbIcon() {
  return (
    <svg width={40} height={40} viewBox="0 0 40 40">
      <circle cx="20" cy="20" r="20" fill="white" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20.4286 10.2857C20.4286 10.7065 20.0875 11.0476 19.6667 11.0476C19.2459 11.0476 18.9048 10.7065 18.9048 10.2857V8.7619C18.9048 8.34112 19.2459 8 19.6667 8C20.0875 8 20.4286 8.34112 20.4286 8.7619V10.2857ZM10.9742 12.6653C10.6519 12.3949 10.6098 11.9143 10.8803 11.5919C11.1508 11.2696 11.6314 11.2276 11.9537 11.498L13.121 12.4775C13.4434 12.748 13.4854 13.2286 13.2149 13.5509C12.9444 13.8733 12.4639 13.9153 12.1415 13.6448L10.9742 12.6653ZM9 18.6667C9 19.0875 9.34112 19.4286 9.7619 19.4286H11.2857C11.7065 19.4286 12.0476 19.0875 12.0476 18.6667C12.0476 18.2459 11.7065 17.9048 11.2857 17.9048H9.7619C9.34112 17.9048 9 18.2459 9 18.6667ZM27.2857 18.6667C27.2857 19.0875 27.6268 19.4286 28.0476 19.4286H29.5714C29.9922 19.4286 30.3333 19.0875 30.3333 18.6667C30.3333 18.2459 29.9922 17.9048 29.5714 17.9048H28.0476C27.6268 17.9048 27.2857 18.2459 27.2857 18.6667ZM27.1918 13.6448L28.3591 12.6653C28.6815 12.3949 28.7235 11.9143 28.453 11.5919C28.1825 11.2696 27.702 11.2276 27.3796 11.498L26.2123 12.4775C25.89 12.748 25.8479 13.2286 26.1184 13.5509C26.3889 13.8733 26.8695 13.9153 27.1918 13.6448ZM19.658 12.5715C18.16 12.5673 16.7139 13.1043 15.6003 14.0784C14.3103 15.2052 13.5729 16.8114 13.5714 18.4974C13.5706 19.87 14.0634 21.1996 14.9646 22.2561C14.9669 22.2561 14.9692 22.257 14.9708 22.2586C14.9725 22.2601 14.9734 22.2623 14.9733 22.2646C15.0707 22.3862 15.1754 22.5021 15.2868 22.6117C16.4376 23.7822 17.072 25.3438 17.0544 26.963C17.0558 27.4299 17.4448 27.8082 17.9252 27.8095H21.4082C21.8885 27.8082 22.2775 27.4299 22.2789 26.963C22.2813 26.5542 22.3191 26.1465 22.3921 25.7439C22.5493 24.9575 22.8596 24.2076 23.3063 23.5344C23.5262 23.1927 23.7832 22.8748 24.0727 22.5863V22.5778C24.1684 22.4847 24.2555 22.3831 24.3426 22.2815C24.3469 22.2773 24.3491 22.2731 24.3513 22.2688C24.3535 22.2646 24.3556 22.2604 24.36 22.2561C25.2649 21.1978 25.7608 19.8652 25.7619 18.489C25.7614 18.1714 25.7352 17.8544 25.6834 17.5408C25.2331 14.9301 23.0667 12.9204 20.3633 12.6054C20.129 12.5815 19.8935 12.5702 19.658 12.5715ZM19.0595 15.1106C19.4181 15.0476 19.6577 14.7059 19.5948 14.3473C19.5318 13.9887 19.1901 13.7491 18.8315 13.812C16.1448 14.2838 14.7143 15.861 14.7143 18.3995C14.7143 18.7636 15.0094 19.0588 15.3735 19.0588C15.7376 19.0588 16.0327 18.7636 16.0327 18.3995C16.0327 16.5234 16.9835 15.4751 19.0595 15.1106ZM17.381 30.8571C17.381 31.6987 18.2631 32.381 18.5155 32.381H20.7871C21.0396 32.381 21.9524 31.6987 21.9524 30.8571V29.3333H17.381V30.8571Z"
        fill={productColor.lochmara}
      />
    </svg>
  );
}
